import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { ShoppingCart, X, Plus, Minus, Trash2, Loader2, CheckCircle2, Copy, Download, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

function OrderSuccessModal({ order, onClose }) {
    const [copied, setCopied] = useState(false);
    const copyCode = () => { if (order.code) { navigator.clipboard.writeText(order.code); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} className="text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Order Complete!</h2>
                <p className="text-sm text-slate-500 mb-5">Your gift card has been delivered</p>
                {order.code && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                        <p className="text-xs text-slate-500 mb-2">Your code</p>
                        <div className="flex items-center justify-between gap-3">
                            <code className="text-lg font-bold text-slate-900 tracking-wider">{order.code}</code>
                            <button onClick={copyCode} className="text-emerald-600">{copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}</button>
                        </div>
                    </div>
                )}
                {order.image && (
                    <a href={order.image.downloadLink} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl mb-4">
                        <Download size={16} /> Download Card Image
                    </a>
                )}
                {order.etransfer && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 text-left">
                        <p className="text-xs text-blue-600 font-semibold mb-2">E-Transfer Instructions</p>
                        <p className="text-sm text-slate-700 mb-1">Send <strong>CA${order.price_cad?.toFixed(2)}</strong> to:</p>
                        <p className="text-base font-bold text-slate-900 mb-2">support@gifttocash.com</p>
                        <p className="text-sm text-slate-700 mb-1">Reference:</p>
                        <code className="text-sm font-bold text-blue-700">{order.order_ref || order.id}</code>
                    </div>
                )}
                <p className="text-xs text-slate-400 mb-5">A copy has been sent to your email.</p>
                <button onClick={onClose} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl">Done</button>
            </div>
        </div>
    );
}

export default function CartPage() {
    const { user } = useAuth();
    const { fmt, currency } = useCurrency();
    const navigate = useNavigate();
    const [cart, setCart] = useState(() => {
        try { return JSON.parse(localStorage.getItem("gtc_cart") || "[]"); } catch { return []; }
    });
    const [payMethod, setPayMethod] = useState("wallet");
    const [buying, setBuying] = useState(false);
    const [error, setError] = useState("");
    const [orderResult, setOrderResult] = useState(null);

    const saveCart = (c) => { setCart(c); localStorage.setItem("gtc_cart", JSON.stringify(c)); };
    const updateQty = (i, delta) => { const c = [...cart]; c[i].qty = Math.max(1, c[i].qty + delta); saveCart(c); };
    const remove = (i) => saveCart(cart.filter((_, ci) => ci !== i));
    const total = cart.reduce((s, i) => s + i.stock.purchasePrice * i.qty, 0);

    const checkout = async () => {
        if (!user) { navigate("/login?redirect=/buy-giftcard/cart"); return; }
        if (cart.length === 0) return;
        setBuying(true); setError("");
        try {
            const results = [];
            for (const item of cart) {
                for (let q = 0; q < item.qty; q++) {
                    if (payMethod === "stripe") {
                        const r = await api.post("/prepaidforge/stripe-checkout", { sku: item.sku, price_eur: item.stock.purchasePrice, code_type: item.stock.type, success_url: window.location.origin + "/buy-giftcard/cart?success=1", cancel_url: window.location.origin + "/buy-giftcard/cart" });
                        window.location.href = r.data.checkout_url;
                        return;
                    } else if (payMethod === "paypal") {
                        const r = await api.post("/prepaidforge/paypal-checkout", { sku: item.sku, price_eur: item.stock.purchasePrice, code_type: item.stock.type });
                        window.location.href = r.data.checkout_url;
                        return;
                    } else if (payMethod === "etransfer") {
                        const r = await api.post("/prepaidforge/etransfer-order", { sku: item.sku, price: item.stock.purchasePrice, code_type: item.stock.type, payment_method: "etransfer" });
                        results.push({ ...r.data, etransfer: true });
                    } else {
                        const r = await api.post("/prepaidforge/order", { sku: item.sku, price: item.stock.purchasePrice, code_type: item.stock.type, payment_method: payMethod });
                        results.push(r.data);
                    }
                }
            }
            saveCart([]);
            setOrderResult(results[0]);
        } catch (err) {
            setError(err.response?.data?.detail || "Checkout failed");
        } finally { setBuying(false); }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <PublicNav />
            {orderResult && <OrderSuccessModal order={orderResult} onClose={() => { setOrderResult(null); navigate("/buy-giftcard"); }} />}
            <div className="max-w-2xl mx-auto px-4 py-8">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/buy-giftcard" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
                        <ArrowLeft size={16} /> Back to Store
                    </Link>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <ShoppingCart size={24} className="text-emerald-500" /> Your Cart
                </h1>
                {cart.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                        <div className="text-5xl mb-4">??</div>
                        <p className="text-slate-500 mb-4">Your cart is empty</p>
                        <Link to="/buy-giftcard" className="bg-emerald-500 text-white font-bold px-6 py-3 rounded-xl inline-block">Browse Gift Cards</Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {cart.map((item, i) => (
                            <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-card p-4 flex items-center gap-4">
                                {item.image && <img src={item.image} alt={item.name} className="w-16 h-16 object-contain rounded-xl bg-slate-50 p-2" />}
                                <div className="flex-1">
                                    <div className="font-semibold text-slate-900">{item.name}</div>
                                    <div className="text-sm text-slate-500">{item.stock.type} code — {fmt(item.stock.purchasePrice)} each</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => updateQty(i, -1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Minus size={12} /></button>
                                    <span className="font-bold w-5 text-center">{item.qty}</span>
                                    <button onClick={() => updateQty(i, 1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Plus size={12} /></button>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-slate-900">{fmt(item.stock.purchasePrice * item.qty)}</div>
                                    <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-600 mt-1"><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 space-y-4">
                            <div className="flex items-center justify-between text-lg font-bold text-slate-900">
                                <span>Total</span>
                                <span>{fmt(total)} <span className="text-sm text-slate-400 font-normal">({currency})</span></span>
                            </div>
                            {!user && (
                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-700 font-medium">
                                    ?? You need to <Link to="/login?redirect=/buy-giftcard/cart" className="underline">login</Link> to checkout
                                </div>
                            )}
                            <div>
                                <p className="text-xs font-semibold text-slate-600 mb-2">Payment method</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[{ id: "wallet", label: "Wallet" }, { id: "stripe", label: "Credit/Debit" }, { id: "paypal", label: "PayPal" }, { id: "etransfer", label: "E-Transfer" }].map(m => (
                                        <button key={m.id} onClick={() => setPayMethod(m.id)}
                                            className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${payMethod === m.id ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-100 text-slate-600 hover:border-slate-200"}`}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {error && <p className="text-sm text-rose-600 bg-rose-50 px-3 py-2 rounded-lg">{error}</p>}
                            <button onClick={checkout} disabled={buying || cart.length === 0}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 text-lg">
                                {buying ? <Loader2 size={20} className="animate-spin" /> : <ShoppingCart size={20} />}
                                {buying ? "Processing..." : user ? `Checkout ${fmt(total)}` : "Login to Checkout"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

